import { gql, useQuery } from '@apollo/client';

import { useTeam } from '../../context/TeamProvider';
import type { NexoyaContentLabelCategory, NexoyaQueryListContentLabelCategoriesArgs } from '../../types';

/**
 * Content Labels v2 — the category list.
 *
 * `listContentLabelCategories(teamId)` is the ONLY root field the contract
 * exposes: there is no `contentLabelCategory(id)`, so every read of a single
 * category goes through this list and picks its id out client-side. The stage
 * counts and the stage pages answer through the same root field, which is why
 * `ContentLabelCategory` / `ContentLabelCategoryValue` are normalized by their
 * uuid ids (`graphql/cache/typePolicies.ts`): every selection below MUST keep
 * `contentLabelCategoryId` / `contentLabelCategoryValueId`, or the cache cannot
 * key the object and the write throws. Only this document (and a category
 * create) writes the ARRAY the page renders; the stage reads keep theirs apart
 * (`stageReads.ts`).
 */

export const CONTENT_LABEL_CATEGORY_VALUE_FRAGMENT = gql`
  fragment ContentLabelCategoryValueFields on ContentLabelCategoryValue {
    contentLabelCategoryValueId
    contentLabelCategoryId
    name
    filters {
      fieldName
      operator
      value {
        string
        stringArr
        number
        numberArr
      }
    }
  }
`;

export const CONTENT_LABEL_CATEGORY_FRAGMENT = gql`
  fragment ContentLabelCategoryFields on ContentLabelCategory {
    contentLabelCategoryId
    teamId
    name
    color
    values {
      ...ContentLabelCategoryValueFields
    }
  }
  ${CONTENT_LABEL_CATEGORY_VALUE_FRAGMENT}
`;

export const LIST_CONTENT_LABEL_CATEGORIES_QUERY = gql`
  query ListContentLabelCategories($teamId: Int!) {
    listContentLabelCategories(teamId: $teamId) {
      ...ContentLabelCategoryFields
    }
  }
  ${CONTENT_LABEL_CATEGORY_FRAGMENT}
`;

/**
 * What the list actually selects. `contentsByAssignmentStage` is left out: it is
 * paged per stage by its own documents, so a `NexoyaContentLabelCategory` here
 * would promise a field this response never carries.
 */
export type ContentLabelCategoryListItem = Omit<NexoyaContentLabelCategory, 'contentsByAssignmentStage'>;

export type ListContentLabelCategoriesResponse = {
  listContentLabelCategories: ContentLabelCategoryListItem[];
};

/**
 * `skip` is how a caller that is not Settings opts out: the seam outside Settings
 * (`src/hooks/useContentLabelCategories`) gates this read on the Content Labels v2
 * flag, and dropping the answer is not the same thing — without a real skip the
 * request still leaves every Performance / Optimize / Content page of every team.
 * It defaults to false, so the existing callers pass nothing and are unaffected.
 */
export function useContentLabelCategoriesQuery({ skip = false }: { skip?: boolean } = {}) {
  const { teamId } = useTeam();

  return useQuery<ListContentLabelCategoriesResponse, NexoyaQueryListContentLabelCategoriesArgs>(
    LIST_CONTENT_LABEL_CATEGORIES_QUERY,
    {
      variables: { teamId },
      skip: skip || !teamId,
    },
  );
}
